import { Divider, Grid, Text } from '@approvalmax/ui/src/components';
import { camelCase } from 'lodash';
import get from 'lodash/get';
import range from 'lodash/range';
import { domain } from 'modules/data';
import { memo, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';

import { PaymentDetailsField } from './components/PaymentDetailsField/PaymentDetailsField';
import { fields } from './PaymentDetailsBankAccountInfo.constants';
import { messages } from './PaymentDetailsBankAccountInfo.messages';
import { PaymentDetailsBankAccountInfoProps } from './PaymentDetailsBankAccountInfo.types';

export const PaymentDetailsBankAccountInfo = memo<PaymentDetailsBankAccountInfoProps>((props) => {
    const { data, status, lastUsedPaymentDetailsProperties, isLoading } = props;

    const normalizedLastUsedPaymentDetailsProperties = useMemo(() => {
        return lastUsedPaymentDetailsProperties?.reduce<Record<string, string>>((acc, property) => {
            // Name contains path to data in bankAccountDetails, but path parts are in PascalCase
            const normalizedName = property.name
                .split('.')
                .map((pathPart) => camelCase(pathPart))
                .join('.');

            acc[`bankAccountDetails.${normalizedName}`] = property.value;

            return acc;
        }, {});
    }, [lastUsedPaymentDetailsProperties]);

    if (isLoading) {
        return (
            <Grid gap={8}>
                {range(5).map((i) => (
                    <Text key={i} font='label' fontSize='small'>
                        <Skeleton inline />
                    </Text>
                ))}
            </Grid>
        );
    }

    if (status === domain.PaymentDetailsPaymentStatus.PaymentDetailsChanged) {
        return (
            <>
                <Grid gridTemplateColumns='1fr 1fr' columnGap={4}>
                    <Text font='body' fontSize='small' fontWeight='medium' color='midnight80' spacing='4'>
                        {messages.previous}
                    </Text>

                    <Text font='body' fontSize='small' fontWeight='medium' color='midnight80' spacing='4'>
                        {messages.actual}
                    </Text>
                </Grid>

                <Divider spacing='2' />

                <Grid gridTemplateColumns='1fr 1fr' columnGap={4}>
                    <Grid gap={8}>
                        {fields.map((field) => {
                            const value =
                                normalizedLastUsedPaymentDetailsProperties?.[field.path] || get(data, field.path);

                            if (!value) {
                                return null;
                            }

                            return (
                                <PaymentDetailsField
                                    key={field.path}
                                    name={field.title}
                                    value={field.getDisplayValue ? field.getDisplayValue(value) : value}
                                    isOld={Boolean(normalizedLastUsedPaymentDetailsProperties?.[field.path])}
                                />
                            );
                        })}
                    </Grid>

                    <Grid gap={8}>
                        {fields.map((field) => {
                            const value = get(data, field.path);

                            if (!value) {
                                return null;
                            }

                            return (
                                <PaymentDetailsField
                                    key={field.path}
                                    name={field.title}
                                    value={field.getDisplayValue ? field.getDisplayValue(value) : value}
                                    isNew={Boolean(normalizedLastUsedPaymentDetailsProperties?.[field.path])}
                                />
                            );
                        })}
                    </Grid>
                </Grid>
            </>
        );
    }

    return (
        <Grid gap={8}>
            {fields.map((field) => {
                const value = get(data, field.path);

                if (!value) {
                    return null;
                }

                return (
                    <PaymentDetailsField
                        key={field.path}
                        name={field.title}
                        value={field.getDisplayValue ? field.getDisplayValue(value) : value}
                    />
                );
            })}
        </Grid>
    );
});

PaymentDetailsBankAccountInfo.displayName = 'PaymentDetailsBankAccountInfo';
